import React, { createRef } from "react";
import {
    Breadcrumb,
    Button,
    Col,
    Container,
    Form,
    Modal,
    Row,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDownload,
    faEdit,
    faPlus,
    faTrash,
} from "@fortawesome/free-solid-svg-icons";

import MyTable from "components/MyTable";
import MySpinner from "components/MySpinner";
import ProcessingButton from "components/ProcessingButton";
import confirm from "components/Confirm";
import Toast from "services/Toast";
import ProductAttributeDictionaryService from "services/ProductAttributeDictionaryService";

const EMPTY_FORM = { id: null, key: "", labels: "", values: "" };
const LOAD_ERROR_MESSAGE =
    "Something went wrong while loading the attributes dictionary";
const CELL_STYLE = { whiteSpace: "pre-wrap", wordBreak: "break-word" };

class Unit extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            initialLoadFailed: false,
            modalMode: null,
            processing: false,
            form: { ...EMPTY_FORM },
        };

        this.mounted = false;
        this.tableRef = createRef();

        this.handleAddOpen = this.handleAddOpen.bind(this);
        this.handleEditOpen = this.handleEditOpen.bind(this);
        this.handleModalClose = this.handleModalClose.bind(this);
        this.handleChangeField = this.handleChangeField.bind(this);
        this.handleModalSubmit = this.handleModalSubmit.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.downloadSpreadsheet = this.downloadSpreadsheet.bind(this);

        this.tableRenderColumns = {
            control: {
                className: "text-center",
                format: (row) =>
                    row.saving ? (
                        <MySpinner />
                    ) : (
                        <>
                            <Button
                                size="sm"
                                className="tdButton mr-1"
                                variant="secondary"
                                onClick={this.handleEditOpen}
                                title="Edit"
                            >
                                <FontAwesomeIcon icon={faEdit} />
                            </Button>
                            <Button
                                size="sm"
                                className="tdButton"
                                variant="danger"
                                onClick={this.handleDelete}
                                title="Delete"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        </>
                    ),
            },
            key: {
                text: (row) => (row.key ?? "").toLowerCase(),
            },
            labels: {
                text: (row) => (row.labels ?? "").toLowerCase(),
                format: (row) => <div style={CELL_STYLE}>{row.labels}</div>,
            },
            values: {
                text: (row) => (row.values ?? "").toLowerCase(),
                format: (row) => <div style={CELL_STYLE}>{row.values}</div>,
            },
        };
    }

    downloadSpreadsheet() {
        this.tableRef.current?.CSV();
    }

    componentDidMount() {
        this.mounted = true;
        ProductAttributeDictionaryService.units
            .all()
            .then((data) => {
                if (!this.mounted) {
                    return;
                }
                if (data === false) {
                    this.handleLoadFailure();
                    return;
                }
                this.tableRef.current?.populate(data);
            })
            .catch(() => {
                if (!this.mounted) {
                    return;
                }
                this.handleLoadFailure();
            });
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    handleLoadFailure() {
        Toast.error(LOAD_ERROR_MESSAGE);
        this.setState({ initialLoadFailed: true }, () => {
            this.tableRef.current?.populate([]);
        });
    }

    getTableHeaders() {
        return [
            {
                field: "key",
                label: "KEY",
                sortable: true,
                searchable: true,
                width: "280px",
            },
            {
                field: "labels",
                label: "Known labels",
                sortable: true,
                searchable: true,
            },
            {
                field: "values",
                label: "Known values",
                sortable: true,
                searchable: true,
            },
            {
                field: "control",
                width: "90px",
                className: "text-center",
                content: (
                    <Button
                        size="sm"
                        className="tdButton"
                        variant="success"
                        onClick={this.handleAddOpen}
                        disabled={this.state.initialLoadFailed}
                        title="New entry"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </Button>
                ),
            },
        ];
    }

    handleAddOpen() {
        this.setState({ modalMode: "add", form: { ...EMPTY_FORM } });
    }

    handleEditOpen(evt) {
        evt.stopPropagation();
        evt.preventDefault();
        const match = this.tableRef.current?.getRowFromEvent(evt);
        const row = match?.row;
        if (!row) {
            return;
        }
        this.setState({
            modalMode: "edit",
            form: {
                id: row.id,
                key: row.key ?? "",
                labels: row.labels ?? "",
                values: row.values ?? "",
            },
        });
    }

    handleModalClose() {
        if (this.state.processing) {
            return;
        }
        this.setState({ modalMode: null });
    }

    handleChangeField(evt) {
        const field = evt.target.getAttribute("name");
        const raw = evt.target.value;
        const value = field === "key" ? raw.toUpperCase() : raw;
        this.setState((prev) => ({
            form: { ...prev.form, [field]: value },
        }));
    }

    canSubmit() {
        const { modalMode, form } = this.state;
        if (form.key.trim().length === 0) {
            return false;
        }
        if (modalMode === "add") {
            return (
                form.labels.trim().length > 0 && form.values.trim().length > 0
            );
        }
        return true;
    }

    handleModalSubmit() {
        if (this.state.modalMode === "add") {
            this.submitAdd();
            return;
        }
        this.submitEdit();
    }

    submitAdd() {
        const { form } = this.state;
        const payload = {
            key: form.key.trim().toUpperCase(),
            labels: form.labels.trim(),
            values: form.values.trim(),
        };
        this.setState({ processing: true });
        ProductAttributeDictionaryService.units
            .create(payload)
            .then((row) => {
                if (!this.mounted) {
                    return;
                }
                if (!row || !row.id) {
                    Toast.error(
                        row?.message ||
                            "Failed to create entry. The key may already exist."
                    );
                    this.setState({ processing: false });
                    return;
                }
                this.setState({ processing: false, modalMode: null }, () => {
                    this.tableRef.current?.addRow(row);
                    Toast.ok(`Entry "${payload.key}" added to the dictionary`);
                });
            })
            .catch(() => {
                if (!this.mounted) {
                    return;
                }
                Toast.error("Failed to create entry.");
                this.setState({ processing: false });
            });
    }

    submitEdit() {
        const { form } = this.state;
        const match = this.tableRef.current?.getRowByID(form.id);
        if (!match?.row) {
            this.setState({ modalMode: null });
            return;
        }
        const key = form.key.trim().toUpperCase();
        const labels = form.labels.trim();
        const values = form.values.trim();
        const payload = {};
        if (key !== (match.row.key ?? "")) {
            payload.key = key;
        }
        if (labels !== (match.row.labels ?? "")) {
            payload.labels = labels;
        }
        if (values !== (match.row.values ?? "")) {
            payload.values = values;
        }
        if (Object.keys(payload).length === 0) {
            this.setState({ modalMode: null });
            return;
        }
        this.setState({ processing: true });
        ProductAttributeDictionaryService.units
            .save(form.id, payload)
            .then((ok) => {
                if (!this.mounted) {
                    return;
                }
                if (!ok) {
                    Toast.error(
                        "Failed to update entry. The key may already exist."
                    );
                    this.setState({ processing: false });
                    return;
                }
                this.tableRef.current?.updateRow({
                    ...match.row,
                    ...payload,
                });
                this.setState({ processing: false, modalMode: null }, () => {
                    Toast.ok("Entry updated");
                });
            })
            .catch(() => {
                if (!this.mounted) {
                    return;
                }
                Toast.error("Failed to update entry.");
                this.setState({ processing: false });
            });
    }

    async handleDelete(evt) {
        evt.stopPropagation();
        evt.preventDefault();
        const match = this.tableRef.current?.getRowFromEvent(evt);
        const row = match?.row;
        if (!row) {
            return;
        }
        const confirmed = await confirm("Are you sure?", "Yes", "Cancel", {
            message: `You're about to remove the entry "${row.key}". This operation can't be undone. Would you like to proceed?`,
        });
        if (!confirmed) {
            return;
        }
        this.tableRef.current?.updateRow({ ...row, saving: true });
        ProductAttributeDictionaryService.units
            .delete(row.id)
            .then((result) => {
                if (!this.mounted) {
                    return;
                }
                if (result === true) {
                    this.tableRef.current?.deleteRow(row);
                    Toast.ok(`Entry "${row.key}" removed from the dictionary`);
                    return;
                }
                Toast.error(result?.message || "Failed to delete this entry");
                this.tableRef.current?.updateRow(row);
            })
            .catch(() => {
                if (!this.mounted) {
                    return;
                }
                Toast.error("Failed to delete this entry");
                this.tableRef.current?.updateRow(row);
            });
    }

    renderModal() {
        const { modalMode, processing, form } = this.state;
        const isAdd = modalMode === "add";
        const isEdit = modalMode === "edit";
        return (
            <Modal
                show={isAdd || isEdit}
                onHide={this.handleModalClose}
                backdrop="static"
                keyboard={!processing}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isAdd ? "New entry" : "Edit entry"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="mb-2">
                        <Col>
                            <Form.Label>Key</Form.Label>
                            <Form.Control
                                type="text"
                                name="key"
                                value={form.key}
                                onChange={this.handleChangeField}
                                readOnly={processing}
                                autoFocus={isAdd}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col>
                            <Form.Label>Known labels</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="labels"
                                value={form.labels}
                                onChange={this.handleChangeField}
                                readOnly={processing}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Form.Label>Known values</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="values"
                                value={form.values}
                                onChange={this.handleChangeField}
                                readOnly={processing}
                            />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer style={{ justifyContent: "center" }}>
                    <Button
                        size="sm"
                        variant="secondary"
                        onClick={this.handleModalClose}
                        disabled={processing}
                    >
                        Cancel
                    </Button>
                    <ProcessingButton
                        size="sm"
                        disabled={!this.canSubmit()}
                        processing={processing}
                        processingLabel={isAdd ? "Adding ..." : "Saving ..."}
                        label={isAdd ? "Add" : "Save"}
                        onClick={this.handleModalSubmit}
                    />
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        return (
            <Container className="pt-4">
                <span style={{ float: "right" }}>
                    <Button
                        disabled={this.state.initialLoadFailed}
                        size="sm"
                        className="tdButton"
                        variant="success"
                        onClick={this.downloadSpreadsheet}
                    >
                        <FontAwesomeIcon icon={faDownload} /> Download
                    </Button>
                </span>
                <Breadcrumb className="dd">
                    <Breadcrumb.Item>Others</Breadcrumb.Item>
                    <Breadcrumb.Item>Attributes Dictionary</Breadcrumb.Item>
                    <Breadcrumb.Item active>Entries</Breadcrumb.Item>
                </Breadcrumb>

                {this.renderModal()}

                <MyTable
                    name="product-attributes-dictionary"
                    ref={this.tableRef}
                    dynamic={false}
                    sortBy="key"
                    sortOrder="asc"
                    headers={this.getTableHeaders()}
                    renderColumns={this.tableRenderColumns}
                    emptyLabel="No entries"
                />
            </Container>
        );
    }
}

export default Unit;
